import { useState } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  Switch,
  Space,
  Popconfirm,
  Typography,
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useUnits, useCreateUnit, useUpdateUnit, useDeleteUnit } from '@hooks/useDictionaries';
import type { UnitOfMeasure, CreateUnitRequest } from '@/types/dictionaries';

export default function UnitsOfMeasurePage() {
  const [mdIsModalOpen, setMdIsModalOpen] = useState(false);
  const [mdEditingUnit, setMdEditingUnit] = useState<UnitOfMeasure | null>(null);
  const [mdForm] = Form.useForm<CreateUnitRequest>();

  const { data: mdUnits, isLoading: mdIsLoading } = useUnits();
  const mdCreateUnit = useCreateUnit();
  const mdUpdateUnit = useUpdateUnit();
  const mdDeleteUnit = useDeleteUnit();

  const mdHandleOpenCreate = () => {
    setMdEditingUnit(null);
    mdForm.resetFields();
    mdForm.setFieldsValue({ isBase: false, decimalPlaces: 0 });
    setMdIsModalOpen(true);
  };

  const mdHandleOpenEdit = (mdUnit: UnitOfMeasure) => {
    setMdEditingUnit(mdUnit);
    mdForm.setFieldsValue(mdUnit);
    setMdIsModalOpen(true);
  };

  const mdHandleSubmit = async () => {
    const mdValues = await mdForm.validateFields();
    if (mdEditingUnit) {
      await mdUpdateUnit.mutateAsync({ ...mdValues, id: mdEditingUnit.id });
    } else {
      await mdCreateUnit.mutateAsync(mdValues);
    }
    setMdIsModalOpen(false);
    mdForm.resetFields();
  };

  const mdHandleDelete = (mdId: string) => {
    mdDeleteUnit.mutate(mdId);
  };

  const mdColumns: ColumnsType<UnitOfMeasure> = [
    {
      title: 'Код',
      dataIndex: 'code',
      key: 'code',
      width: 120,
      sorter: (mdA, mdB) => mdA.code.localeCompare(mdB.code),
    },
    {
      title: 'Назва',
      dataIndex: 'name',
      key: 'name',
      sorter: (mdA, mdB) => mdA.name.localeCompare(mdB.name),
    },
    {
      title: 'Скорочення',
      dataIndex: 'shortName',
      key: 'shortName',
      width: 120,
    },
    {
      title: 'Десяткових знаків',
      dataIndex: 'decimalPlaces',
      key: 'decimalPlaces',
      width: 160,
      align: 'center',
    },
    {
      title: 'Дії',
      key: 'actions',
      width: 120,
      render: (_: unknown, mdRecord: UnitOfMeasure) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => mdHandleOpenEdit(mdRecord)}
          />
          <Popconfirm
            title="Видалити одиницю виміру?"
            description="Якщо одиниця використовується, видалення неможливе."
            onConfirm={() => mdHandleDelete(mdRecord.id)}
            okText="Так"
            cancelText="Ні"
          >
            <Button type="link" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <Typography.Title level={4} style={{ margin: 0 }}>
          Одиниці виміру
        </Typography.Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={mdHandleOpenCreate}>
          Додати одиницю
        </Button>
      </div>

      <Table<UnitOfMeasure>
        columns={mdColumns}
        dataSource={mdUnits}
        rowKey="id"
        loading={mdIsLoading}
        pagination={false}
        size="middle"
      />

      <Modal
        title={mdEditingUnit ? 'Редагувати одиницю виміру' : 'Нова одиниця виміру'}
        open={mdIsModalOpen}
        onOk={mdHandleSubmit}
        onCancel={() => setMdIsModalOpen(false)}
        confirmLoading={mdCreateUnit.isPending || mdUpdateUnit.isPending}
        okText="Зберегти"
        cancelText="Скасувати"
      >
        <Form form={mdForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            name="code"
            label="Код"
            rules={[{ required: true, message: 'Введіть код' }]}
          >
            <Input maxLength={10} />
          </Form.Item>
          <Form.Item
            name="name"
            label="Назва"
            rules={[{ required: true, message: 'Введіть назву' }]}
          >
            <Input maxLength={100} />
          </Form.Item>
          <Form.Item
            name="shortName"
            label="Скорочення"
            rules={[{ required: true, message: 'Введіть скорочення' }]}
          >
            <Input maxLength={20} />
          </Form.Item>
          <Form.Item name="decimalPlaces" label="Десяткових знаків">
            <InputNumber min={0} max={6} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="isBase" label="Базова одиниця" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
